// src/ads/tripAds.js
// Sponsored DMC trips shown in ad slots across RouteChef (components/ads/TripAd.jsx).
// On/off and which slots: FEATURES.ENABLE_TRIP_ADS and FEATURES.TRIP_AD_PLACEMENTS.
//
// Today there's one ad (the Kashmir trip). pickAd() is the one place to change how an ad is chosen
// later: weights, rotation, or matching the slot's context (ctx.destination, ctx.origin, ctx.region),
// e.g. a Kashmir ad on a Delhi to Jammu search, a Kerala ad on a Kochi search.
import { FEATURES } from "../config/features";
import { DMC_SEED_TRIPS } from "../curated/dmc/dmcTrips";
import { initialDmcState, priceDmc, planDays, stayRuns } from "../curated/dmc/dmcEditor";

function adFor(trip, extra = {}) {
    const st = initialDmcState(trip, { pax: 2 });
    const plan = planDays(trip, st);
    const nights = stayRuns(trip, plan).reduce((s, r) => s + r.nights, 0);
    return {
        id: `dmc-${trip.slug}`,
        path: `/dmc/${trip.partner.slug}/${trip.slug}`,
        region: trip.region,
        headline: `${trip.region} in ${plan.length} days`,
        sub: `${String(trip.title).split(":").slice(1).join(":").trim() || trip.subtitle}. Hotels, private cab and your train or flight in one booking.`,
        by: trip.partner?.short_name || trip.partner?.name,
        badge: trip.partner?.tagline ? "Govt approved operator" : "",
        price_pp: priceDmc(trip, st, { plan }).packagePerPerson,
        nights, days: plan.length,
        image: trip.cover_images?.[0]?.src_small || trip.cover_images?.[0]?.src || "",
        image_alt: trip.cover_images?.[0]?.caption || trip.region,
        image_focus: trip.cover_images?.[0]?.focus || "50% 50%",
        // For targeting later: places and cities this ad is most relevant to
        keywords: ["kashmir", "srinagar", "jammu", "katra", "gulmarg", "pahalgam", "sonamarg"],
        weight: 1,
        ...extra,
    };
}

// Kashmir only for now (the brief). Add more DMC trips here, or load them from the partner hub.
export const TRIP_ADS = DMC_SEED_TRIPS.filter((t) => t.region === "Kashmir").map((t) => adFor(t));

export function adsOn(placement) {
    if (!FEATURES.ENABLE_TRIP_ADS || FEATURES.ENABLE_DMC_TRIPS === false) return false;
    const slots = FEATURES.TRIP_AD_PLACEMENTS || {};
    return slots[placement] !== false;
}

/** The ad for one slot, or null. ctx: { exclude: tripSlug, destination, origin } */
export function pickAd(placement, ctx = {}) {
    if (!adsOn(placement)) return null;
    let pool = TRIP_ADS.filter((a) => !ctx.exclude || !a.path.endsWith(`/${ctx.exclude}`));
    if (!pool.length) return null;
    // Later: prefer ads matching the destination being searched. With one ad this changes nothing.
    const dest = String(ctx.destination || "").toLowerCase();
    const matching = dest ? pool.filter((a) => a.keywords.some((k) => dest.includes(k))) : [];
    if (matching.length) pool = matching;
    // Weighted random pick
    const total = pool.reduce((s, a) => s + (a.weight || 1), 0);
    let r = Math.random() * total;
    for (const a of pool) { r -= a.weight || 1; if (r <= 0) return a; }
    return pool[0];
}

export const adHref = (ad, placement) =>
    `${ad.path}?utm_source=routechef&utm_medium=house_ad&utm_campaign=${encodeURIComponent(ad.id)}&utm_content=${encodeURIComponent(placement)}`;

/** Impression and click events: Google Analytics (gtag / dataLayer) if the site has it */
export function adEvent(type, ad, placement) {
    const data = { ad_id: ad.id, ad_placement: placement };
    try {
        if (typeof window.gtag === "function") window.gtag("event", `trip_ad_${type}`, data);
        else if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `trip_ad_${type}`, ...data });
    } catch { /* analytics blocked */ }
}
